import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Download, BookOpen } from 'lucide-react';

// Single source of truth for the handbook content. Re-used by the PDF
// endpoint via /api/handbook/sections so the two stay in lockstep.
export const HANDBOOK = [
  {
    id: 'welcome',
    title: 'Section 1 — Welcome to TRJ Academy™',
    blocks: [
      { h: 'Welcome Message from Dr. Tina Jacobs',
        p: 'Welcome to TRJ Academy™. You are joining a community of professionals committed to lifelong learning, integrity, and the practical mastery of taxation, accounting, and business development. We are proud to have you with us.' },
      { h: 'Mission Statement',
        p: 'To deliver rigorous, practical, and accessible professional education that empowers individuals to advance their careers and serve their communities with excellence.' },
      { h: 'Vision Statement',
        p: 'To be the most trusted online academy for taxation, accounting, and business development professionals.' },
      { h: 'Core Values',
        p: 'Knowledge • Integrity • Leadership. We pursue excellence in every course, ethics in every interaction, and leadership in every classroom.' },
      { h: 'Educational Philosophy',
        p: 'Adult learners thrive when training is practical, sequential, and tied to real-world application. Every program at TRJ Academy™ is designed to move from foundational understanding to applied competency.' },
    ],
  },
  {
    id: 'about',
    title: 'Section 2 — About TRJ Academy™',
    blocks: [
      { h: 'History of TRJ Academy™',
        p: 'TRJ Academy™ was founded as the educational division of TRJ Services, Inc. to formalize and expand the firm\u2019s professional training programs into a national online academy.' },
      { h: 'Relationship to TRJ Services, Inc.',
        p: 'TRJ Academy™ is an educational division of TRJ Services, Inc., providing professional training, certification programs, continuing education, and career development opportunities for aspiring and established professionals.' },
      { h: 'Online Campus Model',
        p: 'All programs are delivered online through our student portal. Coursework includes video lessons, downloadable materials, quizzes, capstone projects, and faculty-graded submissions.' },
      { h: 'Professional Development Focus',
        p: 'Programs are designed for working adults — practical, application-oriented, and aligned with the credentials, software, and procedures used in industry today.' },
    ],
  },
  {
    id: 'conduct',
    title: 'Section 3 — Student Code of Conduct',
    blocks: [
      { h: 'Student Agreement',
        list: [
          'Act professionally in all interactions with staff, faculty, and fellow students.',
          'Respect instructors and students; communicate constructively and courteously.',
          'Maintain academic integrity in every assignment, quiz, and submission.',
          'Avoid plagiarism — submit only your own original work, with appropriate citations.',
          'Protect proprietary course materials; do not redistribute or share without permission.',
          'Comply with all TRJ Academy™ policies and procedures.',
        ] },
      { h: 'Grounds for Disciplinary Action',
        list: [
          'Academic dishonesty (cheating, plagiarism, falsified submissions).',
          'Harassment, threats, or discriminatory behavior toward any community member.',
          'Fraud (impersonation, misrepresentation of identity, payment fraud).',
          'Sharing or redistributing copyrighted course materials.',
          'Misrepresentation of TRJ Academy™ credentials, certificates, or affiliations.',
        ] },
    ],
  },
  {
    id: 'enrollment',
    title: 'Section 4 — Enrollment Policies',
    blocks: [
      { h: 'Enrollment Procedures', p: 'Students enroll through the TRJ Academy™ website, complete payment, acknowledge the Student Handbook, and gain immediate access to their program through their student dashboard.' },
      { h: 'Payment Requirements', p: 'Tuition is paid in full at the time of enrollment unless otherwise stated for an installment plan. Payments are processed securely through PayPal and other approved providers.' },
      { h: 'Access to Courses', p: 'Upon successful payment, students receive lifetime access to their enrolled program materials, subject to the policies herein.' },
      { h: 'Student Responsibilities', p: 'Students are responsible for completing coursework, meeting deadlines, maintaining account security, and abiding by the Code of Conduct.' },
      { h: 'Program Completion Requirements', p: 'Each program defines its own completion criteria — modules, quizzes, capstone, and/or final assessment — published in the program syllabus.' },
    ],
  },
  {
    id: 'refund',
    title: 'Section 5 — Refund Policy',
    blocks: [
      { h: 'Refund Eligibility', p: 'Refund requests may be considered within 72 hours of enrollment if no course content has been accessed and no materials downloaded.' },
      { h: 'Refund Deadlines', p: 'Requests submitted after the 72-hour window are not eligible for refund. Certification programs become non-refundable once access is granted.' },
      { h: 'Non-Refundable Items', p: 'Continuing-education credits, certificates already issued, and any program for which substantial content has been accessed.' },
      { h: 'Administrative Fees', p: 'A standard administrative processing fee may apply to approved refunds.' },
      { h: 'Digital Content Policies', p: 'Digital educational products are generally non-refundable. To request a refund, contact billing@trjacademy.com with your order number and reason.' },
    ],
  },
  {
    id: 'attendance',
    title: 'Section 6 — Attendance & Participation',
    blocks: [
      { h: 'Course Engagement Expectations', p: 'Students are expected to engage actively with all course modules, complete lessons sequentially, and participate in any required submissions.' },
      { h: 'Assignment Submission Requirements', p: 'Assignments and capstone projects must be submitted through the student portal in the formats specified by each course.' },
      { h: 'Completion Deadlines', p: 'Each program has recommended completion timelines. Some certification tracks include hard deadlines posted in the syllabus.' },
      { h: 'Inactivity Policies', p: 'Accounts inactive for extended periods (12+ months) may be flagged for review. TRJ Academy™ may, at its discretion, archive inactive accounts after notice.' },
    ],
  },
  {
    id: 'integrity',
    title: 'Section 7 — Academic Integrity Policy',
    blocks: [
      { h: 'Plagiarism', p: 'All submitted work must be original. Use of another\u2019s work without proper citation is plagiarism and grounds for disciplinary action.' },
      { h: 'Cheating', p: 'Sharing quiz answers, submitting work completed by another, or using unauthorized resources during assessments is prohibited.' },
      { h: 'Unauthorized Collaboration', p: 'Unless an assignment is explicitly designated as a group project, students must complete work independently.' },
      { h: 'AI Usage Policy', p: 'Use of generative AI tools to draft, complete, or substantially produce graded submissions is prohibited unless explicitly permitted by the instructor. When AI is used as a research aid, it must be cited.' },
      { h: 'Citation Requirements', p: 'Use APA or Chicago style for any external sources cited in your work, and credit the original author of any quoted, paraphrased, or summarized material.' },
    ],
  },
  {
    id: 'capstone',
    title: 'Section 8 — Capstone Projects (Tax Professional Certification Program™)',
    blocks: [
      { h: 'Submission Standards', p: 'Capstone projects must be submitted as PDF or DOCX through the student portal, follow the project rubric, and meet all stated formatting and content requirements.' },
      { h: 'Grading Process', p: 'Capstones are reviewed by TRJ Academy™ faculty. Grading criteria include technical accuracy, professional presentation, depth of analysis, and adherence to ethical standards.' },
      { h: 'Revision Process', p: 'If a capstone is returned for revisions, the student receives written feedback and may submit a revised version within the published revision window.' },
      { h: 'Completion Requirements', p: 'Successful capstone approval is required for issuance of the Tax Professional Certification Program™ certificate.' },
    ],
  },
  {
    id: 'businessplan',
    title: 'Section 9 — Business Launch Plan Requirements (Starting a Tax Business™)',
    blocks: [
      { h: 'Submission Format', p: 'The Business Launch Plan must be submitted as PDF or DOCX and address all sections of the published template, including business model, marketing plan, financial projections, and compliance plan.' },
      { h: 'Faculty Review', p: 'Faculty review the plan for completeness, internal consistency, regulatory awareness, and practical readiness.' },
      { h: 'Approval Process', p: 'Plans are marked Approved, Revision Required, or Not Approved. Students receive written feedback for each submission.' },
      { h: 'Resubmission Procedures', p: 'Students may resubmit a revised plan within the published window after feedback is received.' },
    ],
  },
  {
    id: 'credentials',
    title: 'Section 10 — Certificates & Credentials',
    blocks: [
      { h: 'Certificate Issuance', p: 'Certificates are issued upon successful completion of all program requirements and are delivered through the student dashboard with a unique verification number.' },
      { h: 'Certificate Verification', p: 'Each certificate carries a TRJA-YYYY-NNNNNN number and a QR-based verification URL. Employers can verify any certificate at trjacademy.com/verify.' },
      { h: 'Reissuance Procedures', p: 'In the event of a recognized error, change of legal name, or program revision, certificates may be reissued. The original certificate is preserved in our records as superseded.' },
      { h: 'Revocation Policy', p: 'Certificates may be revoked for academic fraud, payment reversal, identity misrepresentation, or other violations of the Code of Conduct.' },
      { h: 'Academic Records', p: 'TRJ Academy™ maintains official academic records of all enrollments, submissions, grades, and certificates issued.' },
      { h: 'Important',
        p: 'Completion of a TRJ Academy™ program does not guarantee employment, licensure, certification by a governmental agency, IRS credentialing, or business success.' },
    ],
  },
  {
    id: 'regulatory',
    title: 'Section 11 — IRS & Regulatory Disclaimer',
    blocks: [
      { p: 'TRJ Academy™ provides educational training. Students are responsible for obtaining any required licenses, registrations, PTINs, EFINs, certifications, or approvals required by federal, state, or local authorities.' },
      { p: 'TRJ Academy™ is not affiliated with the IRS, Prometric, or any governmental agency unless explicitly stated for specific approved programs.' },
    ],
  },
  {
    id: 'records',
    title: 'Section 12 — Student Records',
    blocks: [
      { h: 'Academic Records', p: 'TRJ Academy™ maintains records of enrollment, course progress, submissions, grades, and certificates for each student.' },
      { h: 'Transcript Requests', p: 'Students may view and download their unofficial transcript from the student dashboard. Official transcript requests should be sent to registrar@trjacademy.com.' },
      { h: 'Record Retention', p: 'Academic records are retained in accordance with applicable laws and TRJ Academy™ policies, generally for a minimum of seven years.' },
      { h: 'Privacy Protections', p: 'Student records are kept confidential and disclosed only to authorized personnel or as required by law. See our Privacy Policy for details.' },
    ],
  },
  {
    id: 'tech',
    title: 'Section 13 — Technology Requirements',
    blocks: [
      { h: 'Internet Access', p: 'A reliable broadband connection is required for video lessons and submissions.' },
      { h: 'Device Requirements', p: 'A modern desktop, laptop, or tablet capable of running current browsers. Some assignments may be easier on a desktop or laptop.' },
      { h: 'Browser Requirements', p: 'The latest versions of Chrome, Safari, Edge, or Firefox are recommended. JavaScript and cookies must be enabled.' },
      { h: 'File Upload Requirements', p: 'Capstone, Business Launch Plan, and assignment uploads accept PDF, DOC/DOCX, XLS/XLSX, ZIP, and common image formats up to 25 MB per file.' },
    ],
  },
  {
    id: 'support',
    title: 'Section 14 — Support Services',
    blocks: [
      { p: 'Use the department best matched to your question for the fastest response.', list: [
        'Admissions — admissions@trjacademy.com',
        'Student Support — support@trjacademy.com',
        'Registrar — registrar@trjacademy.com',
        'Billing — billing@trjacademy.com',
        'Faculty — faculty@trjacademy.com',
      ] },
    ],
  },
  {
    id: 'grievance',
    title: 'Section 15 — Grievance Procedure',
    blocks: [
      { h: 'Step 1 — Contact Support', p: 'Start by emailing support@trjacademy.com with a clear description of the issue and any supporting context.' },
      { h: 'Step 2 — Registrar Review', p: 'If the matter involves academic standing, records, or grading, the Registrar will review and respond.' },
      { h: 'Step 3 — Administrative Review', p: 'Unresolved grievances are escalated to TRJ Academy™ administration for a thorough review of facts and policy.' },
      { h: 'Step 4 — Final Determination', p: 'A written final determination is issued. Where applicable, students will be informed of any corrective actions or remedies.' },
    ],
  },
  {
    id: 'acknowledge',
    title: 'Section 16 — Student Acknowledgment',
    blocks: [
      { p: 'I acknowledge that I have read, understood, and agree to comply with the policies and procedures contained within the TRJ Academy™ Student Handbook.' },
    ],
  },
];

export default function StudentHandbookPage() {
  return (
    <div data-testid="handbook-page" className="bg-trjlight">
      <section className="bg-navy text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-50" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
          <span className="trj-divider">Student Resources</span>
          <h1 className="font-display font-semibold text-4xl lg:text-5xl mt-6 leading-tight">
            Student <span className="text-gold font-serif italic">Handbook</span>
          </h1>
          <p className="text-white/75 mt-4 max-w-2xl">
            Your guide to the policies, procedures, and standards of TRJ Academy™. Please read it carefully — you will be asked to acknowledge it at enrollment.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="/api/handbook/pdf" target="_blank" rel="noopener noreferrer">
              <Button className="btn-gold h-11" data-testid="handbook-download-pdf">
                <Download className="h-4 w-4 mr-2" /> Download PDF
              </Button>
            </a>
            <Link to="/contact"><Button variant="outline" className="h-11 text-navy bg-white">Contact Support</Button></Link>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12 grid lg:grid-cols-[220px_1fr] gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-24 text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3 flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5" /> Contents
          </div>
          <nav className="sticky top-32 space-y-1.5 text-sm">
            {HANDBOOK.map((s, i) => (
              <a key={s.id} href={`#${s.id}`} className="block text-slate-600 hover:text-navy hover:underline underline-offset-2">
                {String(i + 1).padStart(2, '0')} · {s.title.split(' — ').slice(1).join(' — ') || s.title}
              </a>
            ))}
          </nav>
        </aside>

        <article className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 space-y-10">
          {HANDBOOK.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <h2 className="font-display font-semibold text-navy text-xl sm:text-2xl border-b border-slate-200 pb-2 mb-4">
                {s.title}
              </h2>
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                {s.blocks.map((b, idx) => (
                  <div key={idx}>
                    {b.h && <div className="font-semibold text-navy mb-1">{b.h}</div>}
                    {b.p && <p>{b.p}</p>}
                    {b.list && (
                      <ul className="list-disc pl-5 space-y-1 mt-1">
                        {b.list.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}

          <div className="border-t border-slate-200 pt-6 text-xs text-slate-500">
            TRJ Academy™ Student Handbook — Revised {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}.
            Operated by TRJ Services, Inc.
          </div>
        </article>
      </div>
    </div>
  );
}
